/**
 * DOCUMENTS JOINTS AUX RÉPONSES — quelles réponses ouvrent un dépôt de
 * fichiers, avec quelle consigne, et lesquelles sont ouvertes pour une réponse
 * donnée.
 *
 * La configuration vit sur la question (`documentsAcceptes`) :
 *   `[{ reponse: "<libellé de la réponse>" | null, consigne: "…" }]`
 * Une réponse est désignée par son LIBELLÉ, comme dans les conseils : c'est ce
 * que le formulaire d'administration manipule. `reponse: null` désigne la
 * question elle-même — une zone de texte, qui n'a pas de réponses à cocher.
 *
 * Module partagé front/back : aucune dépendance à Vue ni à Prisma.
 */
import { libelleOption, valeurOption } from './question-options.js'

/** Les types qui ouvrent un dépôt sur une réponse précise. */
export const TYPES_DOCUMENTS_PAR_REPONSE = new Set(['radio', 'checkbox', 'select'])

/** Les types qui ouvrent un dépôt sur la question entière. */
export const TYPES_DOCUMENTS_PAR_QUESTION = new Set(['textarea', 'text'])

export const CONSIGNE_DOCUMENTS_MAX = 500

/** Taille maximale d'un document joint (20 Mo), et nombre de fichiers par envoi. */
export const TAILLE_MAX_DOCUMENT = 20 * 1024 * 1024
export const NB_MAX_DOCUMENTS_PAR_ENVOI = 10

/** La consigne affichée quand l'administrateur n'en a pas écrit. */
export const CONSIGNE_DOCUMENTS_PAR_DEFAUT = 'Joignez ici vos documents.'

/** La configuration d'une question, toujours sous forme de tableau. */
export function documentsAcceptesDe(question) {
  return Array.isArray(question?.documentsAcceptes) ? question.documentsAcceptes : []
}

/** La configuration qui vise cette réponse (libellé), ou la question si `null`. */
export function documentsPour(question, reponse = null) {
  return documentsAcceptesDe(question).find((d) => (d?.reponse ?? null) === reponse) || null
}

/** La consigne à afficher pour un dépôt. */
export function consigneDe(config) {
  return (config?.consigne || '').trim() || CONSIGNE_DOCUMENTS_PAR_DEFAUT
}

/**
 * Les dépôts OUVERTS pour une réponse : ceux des réponses choisies (questions à
 * choix), ou celui de la question (zone de texte, toujours ouvert).
 *
 * @returns {{ reponse: string|null, consigne: string }[]}
 */
export function depotsOuverts(question, reponse) {
  const config = documentsAcceptesDe(question)
  if (!config.length) return []
  if (!TYPES_DOCUMENTS_PAR_REPONSE.has(question?.type)) {
    const surQuestion = documentsPour(question, null)
    return surQuestion ? [{ reponse: null, consigne: consigneDe(surQuestion) }] : []
  }
  const choisies = Array.isArray(reponse?.selectedOptions) && reponse.selectedOptions.length
    ? reponse.selectedOptions
    : (reponse?.answer ? [reponse.answer] : [])
  const options = Array.isArray(question.options) ? question.options : []
  return options
    .filter((option) => choisies.includes(valeurOption(option)))
    .map((option) => {
      const libelle = libelleOption(option)
      const d = documentsPour(question, libelle)
      return d ? { reponse: libelle, consigne: consigneDe(d) } : null
    })
    .filter(Boolean)
}

/**
 * La configuration nettoyée : une entrée par réponse existante (ou une pour la
 * question, selon le type), consigne bornée. `null` si rien n'est accepté.
 *
 * @param {*} brute ce que le formulaire envoie
 * @param {{ type: string, options?: Array }} question
 */
export function normaliserDocumentsAcceptes(brute, question) {
  if (!Array.isArray(brute)) return null
  const parReponse = TYPES_DOCUMENTS_PAR_REPONSE.has(question?.type)
  const parQuestion = TYPES_DOCUMENTS_PAR_QUESTION.has(question?.type)
  const libelles = new Set((Array.isArray(question?.options) ? question.options : []).map(libelleOption))
  const vus = new Set()
  const propres = []
  for (const entree of brute) {
    if (!entree || typeof entree !== 'object') continue
    const reponse = typeof entree.reponse === 'string' ? entree.reponse.trim() : null
    if (reponse ? !(parReponse && libelles.has(reponse)) : !parQuestion) continue
    const cle = reponse ?? ''
    if (vus.has(cle)) continue
    vus.add(cle)
    const consigne = typeof entree.consigne === 'string' ? entree.consigne.trim().slice(0, CONSIGNE_DOCUMENTS_MAX) : ''
    propres.push({ reponse, consigne })
  }
  return propres.length ? propres : null
}
